<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import format from 'date-fns/format';
import { subDays } from 'date-fns';
import Policy from 'dashboard/components/policy.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { getAccountPeriod } from 'dashboard/helper/accountPeriodHelper';
import ReportsAPI from 'dashboard/api/reports';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';

const { t } = useI18n();
const router = useRouter();
const { accountScopedRoute } = useAccount();

const accountId = useMapGetter('getCurrentAccountId');
const getAccount = useMapGetter('accounts/getAccount');

const conversationsCount = ref(null);
const isLoading = ref(false);

const currentAccount = computed(() => getAccount.value(accountId.value));

const period = computed(() => {
  const account = currentAccount.value;
  if (!account?.id || !account?.created_at) return null;

  return getAccountPeriod(account.period_start_date, account.created_at);
});

const periodRangeLabel = computed(() => {
  if (!period.value) return '';

  const { periodStart, periodEnd } = period.value;
  const periodEndInclusive = subDays(periodEnd, 1);
  const dateFormat = 'MMM d, yyyy';

  return t('SIDEBAR.PERIOD_CONVERSATIONS_RANGE', {
    start: format(periodStart, dateFormat),
    end: format(periodEndInclusive, dateFormat),
  });
});

const reportsRoute = computed(() => {
  if (!period.value) return null;

  const { reportFrom, reportTo } = period.value;

  return accountScopedRoute(
    'conversation_reports',
    {},
    {
      from: reportFrom,
      to: reportTo,
      range: DATE_RANGE_TYPES.CUSTOM_RANGE,
      group_by: 1,
    }
  );
});

const displayCount = computed(() => {
  if (isLoading.value) return null;
  if (conversationsCount.value === null) return '—';

  return conversationsCount.value;
});

const fetchConversationsCount = async () => {
  if (!period.value) return;

  isLoading.value = true;

  try {
    const { reportFrom, reportTo } = period.value;
    const response = await ReportsAPI.getSummary(reportFrom, reportTo);
    conversationsCount.value = response.data.conversations_count ?? 0;
  } catch {
    conversationsCount.value = null;
  } finally {
    isLoading.value = false;
  }
};

watch(period, fetchConversationsCount, { immediate: true });

const openReports = () => {
  if (reportsRoute.value) {
    router.push(reportsRoute.value);
  }
};
</script>

<template>
  <Policy
    :permissions="['administrator', 'report_manage']"
    :feature-flag="FEATURE_FLAGS.REPORTS"
    class="w-full"
  >
    <div
      class="w-full rounded-lg border border-n-weak bg-n-solid-2 p-3 flex flex-col gap-2 z-10"
    >
      <p class="text-xs font-medium text-n-slate-11 mb-0">
        {{ t('SIDEBAR.PERIOD_CONVERSATIONS_TITLE') }}
      </p>
      <p class="text-xs text-n-slate-10 mb-0">
        {{ periodRangeLabel }}
      </p>
      <p
        class="text-2xl font-semibold text-n-slate-12 mb-0"
        :class="{ 'animate-pulse text-n-slate-9': isLoading }"
      >
        {{ displayCount }}
      </p>
      <Button
        v-if="reportsRoute"
        xs
        faded
        primary
        class="w-full justify-center"
        :label="t('SIDEBAR.PERIOD_CONVERSATIONS_VIEW_REPORTS')"
        @click="openReports"
      />
    </div>
  </Policy>
</template>
